import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";

import { ApiError } from "@/api/errors";
import { useAcceptInvitation, useInvitationPreview, useLogin, useLogout, useRegisterInvitation } from "@/api/queries/auth";
import { Button, Field, InlineError, Input, PageLoader, Segmented } from "@/components/ui";
import { formError } from "@/hooks/useFormErrors";
import { applyTenantAccent } from "@/lib/branding";
import { useFragmentToken } from "@/lib/fragmentToken";

import { AuthFrame } from "./AuthFrame";

/** Invitation acceptance. The token lives in the URL fragment (never sent to servers or logs). */
export function InvitePage() {
  const token = useFragmentToken();
  const preview = useInvitationPreview(token);
  const accept = useAcceptInvitation();
  const register = useRegisterInvitation();
  const login = useLogin();
  const logout = useLogout();
  const navigate = useNavigate();
  const [path, setPath] = useState<"create" | "signin">("create");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");

  const data = preview.data;
  useEffect(() => {
    applyTenantAccent(data?.tenant?.palette);
    return () => applyTenantAccent(null);
  }, [data?.tenant]);

  const done = (result: { tenant_slug: string; has_internal_access: boolean }) => {
    navigate(result.has_internal_access ? `/t/${result.tenant_slug}` : "/no-access", { replace: true });
  };

  if (!token) {
    return (
      <AuthFrame>
        <h1 className="text-lg font-semibold">Invitation link incomplete</h1>
        <p className="mt-1 text-sm text-fg-muted">Open the full link from your invitation email.</p>
      </AuthFrame>
    );
  }
  if (preview.isLoading) return <PageLoader label="Checking invitation" />;
  if (!data?.valid) {
    return (
      <AuthFrame footer={<Link className="font-medium text-accent-text hover:underline" to="/login">Go to sign in</Link>}>
        <h1 className="text-lg font-semibold">This invitation is no longer valid</h1>
        <p className="mt-1 text-sm text-fg-muted">It may have expired, been used or been replaced. Ask the person who invited you to send a new one.</p>
      </AuthFrame>
    );
  }

  const tenantName = data.tenant?.name ?? "a workspace";
  const signedIn = data.signed_in_email;
  const error = formError(accept.error ?? register.error ?? login.error);

  return (
    <AuthFrame tenant={data.tenant}>
      <h1 className="text-lg font-semibold text-fg">Join {tenantName}</h1>
      <p className="mt-0.5 mb-4 text-sm text-fg-muted">
        {data.invited_by} invited <span className="font-medium text-fg">{data.email}</span> to K-SHOP.
      </p>
      {error ? (
        <div className="mb-3">
          <InlineError error={error} />
        </div>
      ) : null}

      {signedIn ? (
        signedIn === data.email ? (
          <Button variant="primary" className="w-full" loading={accept.isPending} onClick={() => accept.mutate(token, { onSuccess: done })}>
            Accept invitation
          </Button>
        ) : (
          <div className="flex flex-col gap-3 text-sm">
            <p className="text-fg-muted">
              You're signed in as <span className="font-medium text-fg">{signedIn}</span>. This invitation is for{" "}
              <span className="font-medium text-fg">{data.email}</span>.
            </p>
            <Button onClick={() => logout.mutate(`/invite#token=${token}`)}>Sign out and continue</Button>
          </div>
        )
      ) : (
        <div className="flex flex-col gap-4">
          <Segmented
            legend="How would you like to continue?"
            hideLegend
            value={path}
            onChange={setPath}
            options={[
              { value: "create", label: "Create account" },
              { value: "signin", label: "I have an account" },
            ]}
          />
          {path === "create" ? (
            <form
              noValidate
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                register.mutate({ token, first_name: firstName, last_name: lastName, password }, { onSuccess: done });
              }}
            >
              <Field label="Email">
                <Input value={data.email ?? ""} readOnly disabled autoComplete="username" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name" error={register.error instanceof ApiError ? register.error.fieldError("first_name") : undefined}>
                  <Input autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                </Field>
                <Field label="Last name" optional>
                  <Input autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </Field>
              </div>
              <Field
                label="Password"
                hint="At least 10 characters; avoid common passwords."
                error={register.error instanceof ApiError ? register.error.fieldError("password") : undefined}
              >
                <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </Field>
              <Button type="submit" variant="primary" loading={register.isPending} disabled={!firstName || !password}>
                Create account and join
              </Button>
            </form>
          ) : (
            <form
              noValidate
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                login.mutate(
                  { email: data.email ?? "", password },
                  { onSuccess: () => accept.mutate(token, { onSuccess: done }) },
                );
              }}
            >
              <Field label="Email">
                <Input value={data.email ?? ""} readOnly disabled autoComplete="username" />
              </Field>
              <Field label="Password">
                <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </Field>
              <Button type="submit" variant="primary" loading={login.isPending || accept.isPending} disabled={!password}>
                Sign in and join
              </Button>
            </form>
          )}
        </div>
      )}
    </AuthFrame>
  );
}
